import type { OnDestroy } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Output,
  ViewChild,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  tablerChevronDown,
  tablerChevronRight,
  tablerCheck,
  tablerSearch,
} from '@ng-icons/tabler-icons';
import { ClickOutsideDirective } from '@core/directive/click-outside.directive';
import type { SelectOption } from '@app/types';
import { DropdownPositioner } from '@shared/utils/dropdown-positioner';
import { FormCheckboxComponent } from '@app/components/form-inputs/form-checkbox/form-checkbox.component';
import { PhoneNumberFormatPipe } from '@shared/pipes/phone-number-format.pipe';
import { isPhoneLike } from '@shared/utils/phone-utils';

const SEARCH_VISIBILITY_THRESHOLD = 6;

@Component({
  selector: 'app-searchable-dropdown',
  standalone: true,
  imports: [NgIcon, ClickOutsideDirective, FormCheckboxComponent, PhoneNumberFormatPipe],
  templateUrl: './searchable-dropdown.component.html',
  styleUrl: './searchable-dropdown.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideIcons({ tablerChevronDown, tablerChevronRight, tablerCheck, tablerSearch })],
})
export class SearchableDropdownComponent implements OnDestroy {
  private readonly host = inject(ElementRef<HTMLElement>);

  readonly options = input.required<SelectOption[]>();
  readonly value = input<string | number | Array<string | number> | null | undefined>(undefined);
  readonly mode = input<'single' | 'multi'>('single');
  readonly placeholder = input<string>('Select option');
  readonly searchPlaceholder = input<string>('Search...');
  readonly customTriggerContent = input<string | undefined>(undefined);
  readonly disabled = input<boolean>(false);
  readonly triggerAppearance = input<'default' | 'minimal' | 'compact'>('default');
  readonly formatPhoneNumbers = input<boolean>(false);
  readonly alwaysShowSearch = input<boolean>(false);
  readonly optionVariant = input<'selection' | 'navigation'>('selection');
  /** When `end`, menu is anchored to the trigger's right edge and grows left (viewport-safe for controls near the right edge). */
  readonly panelHorizontalAlign = input<'start' | 'end'>('start');

  @Output() readonly selectionChange = new EventEmitter<
    string | number | Array<string | number> | null
  >();

  @ViewChild('dropdownMenu', { read: ElementRef })
  dropdownMenu?: ElementRef<HTMLElement>;

  readonly isOpen = signal(false);
  readonly searchTerm = signal('');
  private readonly selectedValueSignal = signal<string | number | Array<string | number> | null>(
    null,
  );
  readonly selectedValue = this.selectedValueSignal.asReadonly();

  private dropdownPositioner: DropdownPositioner | null = null;

  constructor() {
    effect(() => {
      const v = this.value();
      if (v !== undefined) {
        this.selectedValueSignal.set(v);
      }
    });
  }

  readonly showSearch = computed(() => {
    return this.alwaysShowSearch() || this.options().length > SEARCH_VISIBILITY_THRESHOLD;
  });

  readonly filteredOptions = computed((): SelectOption[] => {
    const term = this.searchTerm().toLowerCase().trim();
    const list = this.options();
    if (term.length === 0) {
      return list;
    }
    return list.filter((opt) => opt.label.toLowerCase().includes(term));
  });

  readonly displayText = computed((): string => {
    const custom = this.customTriggerContent();
    if (custom !== undefined && custom !== '') {
      return custom;
    }
    const value = this.selectedValue();
    const opts = this.options();
    const placeholderText = this.placeholder();
    const currentMode = this.mode();

    if (value === null || value === undefined) {
      return placeholderText;
    }

    if (currentMode === 'single') {
      const selectedOption = opts.find((opt) => opt.value === value);
      return selectedOption?.label ?? placeholderText;
    } else {
      if (Array.isArray(value) && value.length > 0) {
        return `Selected (${value.length})`;
      }
      return placeholderText;
    }
  });

  ngOnDestroy(): void {
    this.destroyDropdownPositioner();
  }

  toggle(): void {
    if (this.disabled()) {
      return;
    }

    this.isOpen.update((value) => !value);

    if (this.isOpen()) {
      setTimeout(() => {
        this.initDropdownPositioner();
      }, 0);
    } else {
      this.searchTerm.set('');
      this.destroyDropdownPositioner();
    }
  }

  onSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchTerm.set(target.value);
  }

  onOutsideClick(): void {
    if (this.isOpen()) {
      this.isOpen.set(false);
      this.searchTerm.set('');
      this.destroyDropdownPositioner();
    }
  }

  isSelected(option: SelectOption): boolean {
    const value = this.selectedValue();

    if (this.mode() === 'single') {
      return value === option.value;
    } else {
      if (Array.isArray(value)) {
        return value.includes(option.value);
      }
      return false;
    }
  }

  selectSingle(option: SelectOption): void {
    if (option.disabled === true) {
      return;
    }

    this.selectedValueSignal.set(option.value);
    this.selectionChange.emit(option.value);
    this.isOpen.set(false);
    this.searchTerm.set('');
    this.destroyDropdownPositioner();
  }

  toggleMulti(option: SelectOption): void {
    if (option.disabled === true) {
      return;
    }

    const currentValue = this.selectedValue();
    let newValue: Array<string | number>;

    if (Array.isArray(currentValue)) {
      if (currentValue.includes(option.value)) {
        newValue = currentValue.filter((v) => v !== option.value);
      } else {
        newValue = [...currentValue, option.value];
      }
    } else {
      newValue = [option.value];
    }

    this.selectedValueSignal.set(newValue);
    this.selectionChange.emit(newValue);
  }

  onSearchClick(event: Event): void {
    event.stopPropagation();
  }

  isPhoneLabel(value: string): boolean {
    return this.formatPhoneNumbers() && isPhoneLike(value);
  }

  private initDropdownPositioner(): void {
    if (this.dropdownMenu === undefined || this.dropdownMenu === null) {
      return;
    }

    const triggerElement = this.host.nativeElement.querySelector('.searchable-dropdown-trigger');
    if (triggerElement === null) {
      return;
    }

    this.dropdownPositioner = new DropdownPositioner(
      this.dropdownMenu.nativeElement,
      triggerElement as HTMLElement,
      () => this.onOutsideClick(),
    );

    this.dropdownPositioner.adjust();
    this.dropdownPositioner.startListening();
  }

  private destroyDropdownPositioner(): void {
    if (this.dropdownPositioner !== null) {
      this.dropdownPositioner.stopListening();
      this.dropdownPositioner = null;
    }
  }
}
